import { useAuthContext } from '../contexts/AuthContext';

/**
 * Hook personalizado para usar el contexto de autenticación
 * Proporciona acceso fácil a todas las funcionalidades de auth
 * 
 * @returns {Object} Objeto con propiedades y métodos de autenticación
 */
export const useAuth = () => {
  const context = useAuthContext();
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Hook para obtener información específica del usuario autenticado
 * Útil cuando solo necesitas datos del usuario sin funcionalidades
 * 
 * @returns {Object} Información del usuario actual
 */
export const useCurrentUser = () => {
  const { user, isAuthenticated, userDisplayName, userEmail, userId, isEmailVerified } = useAuth();
  
  return {
    user,
    isAuthenticated,
    userDisplayName,
    userEmail,
    userId,
    isEmailVerified
  };
};

/**
 * Hook para acciones de autenticación
 * Útil cuando solo necesitas funciones de auth sin estado
 * 
 * @returns {Object} Funciones de autenticación
 */
export const useAuthActions = () => {
  const { login, register, logout, resetPassword, clearError } = useAuth();
  
  return {
    login,
    register,
    logout,
    resetPassword,
    clearError
  };
};

/**
 * Hook para estado de autenticación
 * Útil para mostrar loading states y errores
 * 
 * @returns {Object} Estado de la autenticación
 */
export const useAuthState = () => {
  const { loading, error, isAuthenticated } = useAuth();
  
  return {
    loading,
    error,
    isAuthenticated
  };
};

export default useAuth;
